import { ChangeEvent, useMemo, useState } from 'react';

import { Box, TextField } from '@mui/material';
import { ClassNameMap } from '@mui/styles';

import { TableFilter } from '../../Table.types';

const getActiveCondition = (restriction: string) => (restriction === 'min' ? 'lte' : 'gte');

const isValueValidBasedOnRestriction = (
  restriction: string,
  currentValue: number,
  restrictionValue: number,
) =>
  restrictionValue === undefined ||
  restrictionValue === null ||
  (restriction === 'min' && restrictionValue <= Number(currentValue)) ||
  (restriction === 'max' && restrictionValue >= Number(currentValue));

const isValueValidBasedOnMinAndMaxValue = (
  currentValue: number,
  maxValue: number,
  minValue: number,
) => {
  const hasCurrentValue = currentValue !== undefined && currentValue !== null;
  const hasMinValue = minValue !== undefined && minValue !== null;
  const hasMaxValue = maxValue !== undefined && maxValue !== null;
  if (!hasCurrentValue || (!hasMaxValue && !hasMinValue)) {
    return true;
  }

  if (hasMaxValue && !hasMinValue) {
    return maxValue >= currentValue;
  }

  if (hasMinValue && !hasMaxValue) {
    return minValue <= currentValue;
  }

  return minValue <= currentValue && currentValue <= maxValue;
};

type GreaternessNumberFilterControlProps = {
  filter: TableFilter;
  activeFilters: Record<string, string>;
  restriction: 'min' | 'max';
  restrictionValue: any;
  classes: ClassNameMap;
  onFilterChange: (condition: string, value: any, isFilterValid?: boolean) => any;
  onClose: (e: any) => void;
};

export const GreaternessNumberFilterControl = ({
  filter,
  activeFilters,
  restriction,
  restrictionValue,
  classes,
  onFilterChange,
  onClose,
}: GreaternessNumberFilterControlProps) => {
  const [activeCondition] = useState(getActiveCondition(restriction));

  const maxValueRestriction = useMemo(() => filter.conditions[0]?.maxValue, [filter]);
  const minValueRestriction = useMemo(() => filter.conditions[0]?.minValue, [filter]);

  const [isRestrictionValidationIssue, setRestrictionValidationIssue] = useState(false);
  const [isMinMaxValueValidationIssue, setIsMinMaxValueValidationIssue] = useState(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value) {
      value = value.replaceAll(' ', '');
      const isValid = /^[0-9]+$/.test(value);

      const restrictionValidationIssue = !isValueValidBasedOnRestriction(
        restriction,
        Number(value),
        restrictionValue,
      );
      const minMaxValueValidationIssue = !isValueValidBasedOnMinAndMaxValue(
        Number(value),
        maxValueRestriction,
        minValueRestriction,
      );

      setRestrictionValidationIssue(restrictionValidationIssue);
      setIsMinMaxValueValidationIssue(minMaxValueValidationIssue);

      isValid &&
        onFilterChange(
          activeCondition,
          value,
          restrictionValidationIssue || minMaxValueValidationIssue,
        );
    } else {
      onFilterChange(activeCondition, value, false);
      setRestrictionValidationIssue(false);
      setIsMinMaxValueValidationIssue(false);
    }
  };

  const value = activeFilters[activeCondition] || '';

  return (
    <Box display="flex" flexDirection="column" className={classes.filterInput}>
      <TextField
        autoFocus
        data-testid={`${filter.type}-filter`}
        fullWidth
        onChange={handleInputChange}
        error={isRestrictionValidationIssue || isMinMaxValueValidationIssue}
        helperText={
          isRestrictionValidationIssue
            ? `"${filter.label}" number should be ${
                restriction === 'min' ? 'greater' : 'less'
              } than ${restrictionValue}`
            : isMinMaxValueValidationIssue
              ? `"${filter.label}" number should be greater than or equal to ${minValueRestriction} and should be less than or equal to ${maxValueRestriction}`
              : ''
        }
        onKeyPress={e => {
          if (e.key !== 'Enter') {
            return;
          }
          if (isRestrictionValidationIssue || isMinMaxValueValidationIssue) {
            return;
          }

          onFilterChange(
            activeCondition,
            activeFilters[activeCondition],
            isRestrictionValidationIssue || isMinMaxValueValidationIssue,
          );
          onClose(e);
        }}
        inputProps={{
          pattern: /^[0-9]+$/,
        }}
        placeholder={filter.label}
        value={value}
      />
    </Box>
  );
};
